// Multi-form workbench — split-screen pane manager.
// Mounts on /app/e16-workbench (or /app/erpnext16-workbench / /workbench).
// Each pane = <iframe src="/app/<doctype>/<name>?embed=1">.

import { theme } from "./prefs.js";
import { t } from "../utils/i18n.js";

function isWorkbenchRoute() {
	const p = window.location.pathname;
	return (
		p.endsWith("/e16-workbench") || p.endsWith("/e16-workbench/") ||
		p.endsWith("/erpnext16-workbench") || p.endsWith("/erpnext16-workbench/") ||
		p.endsWith("/workbench") || p.endsWith("/workbench/")
	);
}

function applyEmbedClass() {
	try {
		const u = new URL(window.location.href);
		if (u.searchParams.get("embed") === "1") {
			document.body.classList.add("e16-embed");
		}
	} catch (e) {}
}

function makePane(spec) {
	const lng = theme.currentLang();
	const pane = document.createElement("div");
	pane.className = "e16-pane";
	pane.dataset.id = spec.id;

	const header = document.createElement("div");
	header.className = "e16-pane__header";
	header.innerHTML = `
		<div class="e16-pane__title">${spec.doctype && spec.name ? `${spec.doctype} — ${spec.name}` : t("Empty pane", lng)}</div>
		<div class="e16-pane__actions">
			<button type="button" data-act="reload" title="Reload">⟳</button>
			<button type="button" data-act="close" title="Close">✕</button>
		</div>`;
	pane.appendChild(header);

	const body = document.createElement("div");
	body.className = "e16-pane__body";
	if (spec.doctype && spec.name) {
		const slug = spec.doctype.toLowerCase().replace(/ /g, "-");
		const iframe = document.createElement("iframe");
		iframe.src = `/app/${encodeURIComponent(slug)}/${encodeURIComponent(spec.name)}?embed=1`;
		body.appendChild(iframe);
		header.querySelector('[data-act="reload"]').addEventListener("click", () => { iframe.src = iframe.src; });
	} else {
		const empty = document.createElement("div");
		empty.className = "e16-pane__empty";
		empty.innerHTML = `<div>
			<div style="font-size:18px;margin-bottom:8px;">${t("Empty pane", lng)}</div>
			<div style="opacity:0.7">${t("Pick a doctype to load…", lng)}</div>
		</div>`;
		body.appendChild(empty);
	}
	pane.appendChild(body);

	header.querySelector('[data-act="close"]').addEventListener("click", () => pane.remove());

	return pane;
}

function buildShell(container) {
	const lng = theme.currentLang();
	container.innerHTML = "";
	container.classList.add("e16-workbench");

	const toolbar = document.createElement("div");
	toolbar.className = "e16-workbench__toolbar";
	toolbar.innerHTML = `
		<button type="button" class="btn btn-primary btn-sm" data-act="add">+ ${t("Add Pane", lng)}</button>
		<select data-act="layout" class="form-control" style="width:auto;">
			<option value="cols-1">1</option>
			<option value="cols-2" selected>2</option>
			<option value="cols-3">3</option>
			<option value="grid-2x2">2×2</option>
		</select>`;

	const panes = document.createElement("div");
	panes.className = "e16-workbench__panes e16-workbench__panes--cols-2";

	container.appendChild(toolbar);
	container.appendChild(panes);

	panes.appendChild(makePane({ id: "p1" }));
	panes.appendChild(makePane({ id: "p2" }));

	toolbar.querySelector('[data-act="add"]').addEventListener("click", () => {
		panes.appendChild(makePane({ id: "p" + (panes.children.length + 1) }));
	});
	toolbar.querySelector('[data-act="layout"]').addEventListener("change", (ev) => {
		panes.className = "e16-workbench__panes e16-workbench__panes--" + ev.target.value;
	});

	try {
		if (window.frappe && frappe.realtime) {
			frappe.realtime.on("doc_update", () => {
				panes.querySelectorAll("iframe").forEach((f) => { try { f.src = f.src; } catch (e) {} });
			});
		}
	} catch (e) {}
}

function ensureMount() {
	let host = document.getElementById("e16-workbench-mount");
	if (host) return host;
	host = document.createElement("div");
	host.id = "e16-workbench-mount";
	const main = document.querySelector(".main-section, #body, body");
	(main || document.body).appendChild(host);
	return host;
}

export const workbench = {
	mount() {
		applyEmbedClass();
		const prefs = (window.frappe?.boot?.e16_prefs) || {};
		if (prefs.enable_workbench === 0) return;

		const tryBuild = () => {
			if (!isWorkbenchRoute()) return;
			const host = ensureMount();
			if (host.dataset.built) return;
			host.dataset.built = "1";
			buildShell(host);
		};

		tryBuild();
		window.addEventListener("hashchange", tryBuild);
		try {
			if (window.frappe && frappe.router) {
				frappe.router.on("change", tryBuild);
			}
		} catch (e) {}
	},
};
